import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Chip,
  Avatar,
  Stack,
  Tooltip,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Divider,
} from '@mui/material';
import {
  Image as ImageIcon,
  Visibility as VisibilityIcon,
  BrushOutlined,
  Crop as CropIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Share as ShareIcon,
  PictureAsPdf as PdfIcon,
  Description as DocumentIcon,
  VideoFile as VideoIcon,
  AudioFile as AudioIcon,
  Archive as ArchiveIcon,
  Code as CodeIcon,
  TextFields as TextIcon,
  TableChart as ExcelIcon,
  Slideshow as PowerPointIcon,
  InsertDriveFile as GenericFileIcon,
  MoreVert as MoreVertIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

interface AttachmentItem {
  id: number | string;
  type: 'image' | 'document';
  name: string;
  filename?: string;
  original_filename?: string;
  image_path?: string;
  uploaded_at?: string;
  created_at?: string;
  size?: number;
  file_size?: number;
  mime_type?: string;
  document_type?: string;
}

interface AttachmentListProps {
  items: AttachmentItem[];
  getDocumentIcon: (mimeType?: string, filename?: string) => React.ReactElement;
  formatDate: (dateString: string) => string;
  onImageClick?: (item: AttachmentItem) => void;
  onEditImage?: (item: AttachmentItem, index?: number) => void;
  onCropImage?: (item: AttachmentItem, index?: number) => void;
  onDownloadImage?: (item: AttachmentItem) => void;
  onDeleteImage?: (id: number | string, index?: number) => void;
  onDownloadDocument?: (item: AttachmentItem) => void;
  onShareDocument?: (item: AttachmentItem) => void;
  onDeleteDocument?: (id: number | string, index?: number) => void;
  showEditActions?: boolean;
  showCropActions?: boolean;
  showCreateInvoice?: boolean;
  onCreateInvoice?: (item: AttachmentItem) => void;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({
  items,
  getDocumentIcon,
  formatDate,
  onImageClick,
  onEditImage,
  onCropImage,
  onDownloadImage,
  onDeleteImage,
  onDownloadDocument,
  onShareDocument,
  onDeleteDocument,
  showEditActions = true,
  showCropActions = true,
  showCreateInvoice = false,
  onCreateInvoice,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [menuAnchorEl, setMenuAnchorEl] = useState<{[key: string]: HTMLElement | null}>({});

  const handleMenuOpen = (itemId: string | number, event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(prev => ({ ...prev, [itemId]: event.currentTarget }));
  };

  const handleMenuClose = (itemId: string | number) => {
    setMenuAnchorEl(prev => ({ ...prev, [itemId]: null }));
  };

  const getFileTypeIcon = (item: AttachmentItem) => {
    if (item.type === 'image') {
      return <ImageIcon sx={{ color: '#1976d2' }} />;
    }

    const filename = item.filename || item.name || '';
    const extension = filename.split('.').pop()?.toLowerCase() || '';
    const mimeType = item.mime_type?.toLowerCase() || '';

    if (extension === 'pdf' || mimeType.includes('pdf')) {
      return <PdfIcon sx={{ color: '#d32f2f' }} />;
    }

    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension) || 
        mimeType.startsWith('image/')) {
      return <ImageIcon sx={{ color: '#1976d2' }} />;
    }

    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(extension) ||
        mimeType.startsWith('video/')) {
      return <VideoIcon sx={{ color: '#9c27b0' }} />;
    }

    if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'm4a'].includes(extension) ||
        mimeType.startsWith('audio/')) {
      return <AudioIcon sx={{ color: '#ff9800' }} />;
    }

    if (['doc', 'docx'].includes(extension) || 
        mimeType.includes('msword') || mimeType.includes('wordprocessingml')) {
      return <DocumentIcon sx={{ color: '#2196f3' }} />;
    }

    if (['xls', 'xlsx'].includes(extension) || 
        mimeType.includes('excel') || mimeType.includes('spreadsheetml')) {
      return <ExcelIcon sx={{ color: '#4caf50' }} />;
    }

    if (['ppt', 'pptx'].includes(extension) || 
        mimeType.includes('powerpoint') || mimeType.includes('presentationml')) {
      return <PowerPointIcon sx={{ color: '#ff5722' }} />;
    }

    if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2'].includes(extension) ||
        mimeType.includes('zip') || mimeType.includes('archive')) {
      return <ArchiveIcon sx={{ color: '#795548' }} />;
    }

    if (['js', 'ts', 'jsx', 'tsx', 'html', 'css', 'scss', 'py', 'java', 'cpp', 'c', 'php', 'rb', 'go', 'rs'].includes(extension)) {
      return <CodeIcon sx={{ color: '#607d8b' }} />;
    }

    if (['txt', 'md', 'rtf', 'log'].includes(extension) || 
        mimeType.startsWith('text/')) {
      return <TextIcon sx={{ color: '#424242' }} />;
    }

    return <GenericFileIcon sx={{ color: '#757575' }} />;
  };

  const getDateString = (item: AttachmentItem): string => {
    const dateStr = item.uploaded_at || item.created_at || new Date().toISOString();
    return formatDate(dateStr);
  };

  const getFileSize = (item: AttachmentItem): string => {
    const size = item.size || item.file_size;
    if (!size) return '';
    return `${(size / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {items.map((item, index) => (
        <Paper
          key={`${item.type}-${item.id || index}`}
          variant='outlined'
          sx={{
            borderRadius: isMobile ? 1.5 : 2,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: isMobile 
                ? '0 2px 8px rgba(0, 0, 0, 0.1)' 
                : '0 4px 12px rgba(0, 0, 0, 0.1)',
              transform: isMobile ? 'translateY(-1px)' : 'translateY(-2px)',
            },
          }}
        >
          <ListItem
            sx={{
              py: isMobile ? 1.5 : 2,
              px: isMobile ? 2 : 3,
              minHeight: isMobile ? 80 : 80,
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: isMobile ? 1.5 : 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'action.hover',
                '& .attachment-actions': {
                  opacity: 1,
                },
              },
              '& .attachment-actions': {
                opacity: isMobile ? 1 : 0.7,
                transition: 'opacity 0.2s ease-in-out',
              },
            }}
          >
              {/* Header mit Icon und Dateiname + Mobile Menu */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
              }}>
                {/* Icon für beide Desktop und Mobile */}
                <Avatar
                  sx={{
                    width: isMobile ? 40 : 50,
                    height: isMobile ? 40 : 50,
                    backgroundColor: 'transparent',
                    border: isMobile ? '1.5px solid' : '2px solid',
                    borderColor: 'divider',
                    flexShrink: 0,
                    '& .MuiSvgIcon-root': {
                      fontSize: isMobile ? '1.2rem' : '1.5rem',
                    },
                  }}
                >
                  {getFileTypeIcon(item)}
                </Avatar>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                    <Typography 
                      variant={isMobile ? 'body2' : 'body1'}
                      fontWeight={600}
                      sx={{
                        maxWidth: isMobile ? 180 : 280,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: 'text.primary',
                      }}
                    >
                      {item.type === 'image' 
                        ? (item.original_filename || item.filename || `Bild-${item.id}`)
                        : (item.filename || item.name)
                      }
                    </Typography>
                  </Box>
                  
                  <Box>
                    <Typography variant={isMobile ? 'caption' : 'body2'} color='text.secondary' sx={{ fontWeight: 500 }}>
                      {getDateString(item)}
                    </Typography>
                    {item.type === 'document' && getFileSize(item) && (
                      <Typography variant='caption' color='text.secondary' sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 0.5,
                        mt: 0.25 
                      }}>
                        {getFileSize(item)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Desktop: Action Buttons rechts neben dem Text */}
                {!isMobile && (
                  <Stack direction='row' spacing={0.5} className="attachment-actions" sx={{ flexShrink: 0 }}>
                    {item.type === 'image' ? (
                      <>
                        {onImageClick && (
                          <Tooltip title="Ansehen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onImageClick(item)}
                              sx={{ 
                                backgroundColor: 'transparent',
                                color: 'primary.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(21, 101, 192, 0.15)',
                                  color: 'primary.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {showEditActions && onEditImage && (
                          <Tooltip title="Bearbeiten" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onEditImage(item, index)}
                              sx={{ 
                                backgroundColor: 'transparent',
                                color: 'secondary.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(75, 75, 74, 0.15)',
                                  color: 'secondary.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <BrushOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {showCropActions && onCropImage && (
                          <Tooltip title="Zuschneiden" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onCropImage(item, index)}
                              sx={{ 
                                backgroundColor: 'transparent',
                                color: 'info.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 150, 243, 0.15)',
                                  color: 'info.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <CropIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDownloadImage && (
                          <Tooltip title="Herunterladen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onDownloadImage(item)}
                              sx={{ 
                                backgroundColor: 'transparent',
                                color: 'success.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(76, 175, 80, 0.15)',
                                  color: 'success.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDeleteImage && (
                          <Tooltip title="Löschen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onDeleteImage(item.id, index)}
                              sx={{ 
                                backgroundColor: 'transparent',
                                color: 'error.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                  color: 'error.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    ) : (
                      <>
                        {/* Create Invoice button for RAPPORT documents */}
                        {showCreateInvoice && item.document_type === 'RAPPORT' && onCreateInvoice && (
                          <Tooltip title="Rechnung erstellen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onCreateInvoice(item)}
                              sx={{
                                backgroundColor: 'transparent',
                                color: 'warning.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 152, 0, 0.15)',
                                  color: 'warning.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <ReceiptIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onShareDocument && (
                          <Tooltip title="Per E-Mail teilen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onShareDocument(item)}
                              sx={{
                                backgroundColor: 'transparent',
                                color: 'info.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(33, 150, 243, 0.15)',
                                  color: 'info.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDownloadDocument && (
                          <Tooltip title="Herunterladen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onDownloadDocument(item)}
                              sx={{
                                backgroundColor: 'transparent',
                                color: 'success.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(76, 175, 80, 0.15)',
                                  color: 'success.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {onDeleteDocument && (
                          <Tooltip title="Löschen" arrow>
                            <IconButton
                              size="small"
                              onClick={() => onDeleteDocument(item.id, index)}
                              sx={{
                                backgroundColor: 'transparent',
                                color: 'error.main',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: 'rgba(244, 67, 54, 0.15)',
                                  color: 'error.main',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease-in-out',
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                  </Stack>
                )}

                {/* Mobile: Dreipunkte-Menü rechts */}
                {isMobile && (
                  <>
                    <IconButton
                      onClick={(e) => handleMenuOpen(item.id, e)}
                      sx={{
                        color: 'text.secondary',
                        backgroundColor: 'transparent',
                        flexShrink: 0,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                          color: 'primary.main',
                        },
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    
                    <Menu
                      anchorEl={menuAnchorEl[item.id]}
                      open={Boolean(menuAnchorEl[item.id])}
                      onClose={() => handleMenuClose(item.id)}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                      PaperProps={{
                        sx: {
                          minWidth: 180,
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                          border: '1px solid',
                          borderColor: 'divider',
                        }
                      }}
                    >
                      {item.type === 'image' ? (
                        <>
                          {onImageClick && (
                            <MenuItem 
                              onClick={() => {
                                onImageClick(item);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <VisibilityIcon fontSize="small" color="primary" />
                              <Typography>Ansehen</Typography>
                            </MenuItem>
                          )}
                          {showEditActions && onEditImage && (
                            <MenuItem 
                              onClick={() => {
                                onEditImage(item, index);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <BrushOutlined fontSize="small" sx={{ color: 'secondary.main' }} />
                              <Typography>Bearbeiten</Typography>
                            </MenuItem>
                          )}
                          {showCropActions && onCropImage && (
                            <MenuItem 
                              onClick={() => {
                                onCropImage(item, index);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <CropIcon fontSize="small" sx={{ color: 'info.main' }} />
                              <Typography>Zuschneiden</Typography>
                            </MenuItem>
                          )}
                          {onDownloadImage && (
                            <MenuItem 
                              onClick={() => {
                                onDownloadImage(item);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <DownloadIcon fontSize="small" sx={{ color: 'success.main' }} />
                              <Typography>Herunterladen</Typography>
                            </MenuItem>
                          )}
                          {onDeleteImage && (
                            <>
                              <Divider sx={{ my: 0.5 }} />
                              <MenuItem 
                                onClick={() => {
                                  onDeleteImage(item.id, index);
                                  handleMenuClose(item.id);
                                }}
                                sx={{ gap: 1.5, py: 1.5 }}
                              >
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                                <Typography color="error.main">Löschen</Typography>
                              </MenuItem>
                            </>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Create Invoice menu item for RAPPORT documents */}
                          {showCreateInvoice && item.document_type === 'RAPPORT' && onCreateInvoice && (
                            <MenuItem
                              onClick={() => {
                                onCreateInvoice(item);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <ReceiptIcon fontSize="small" sx={{ color: 'warning.main' }} />
                              <Typography>Rechnung erstellen</Typography>
                            </MenuItem>
                          )}
                          {onDownloadDocument && (
                            <MenuItem
                              onClick={() => {
                                onDownloadDocument(item);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <DownloadIcon fontSize="small" sx={{ color: 'success.main' }} />
                              <Typography>Herunterladen</Typography>
                            </MenuItem>
                          )}
                          {onShareDocument && (
                            <MenuItem
                              onClick={() => {
                                onShareDocument(item);
                                handleMenuClose(item.id);
                              }}
                              sx={{ gap: 1.5, py: 1.5 }}
                            >
                              <ShareIcon fontSize="small" sx={{ color: 'info.main' }} />
                              <Typography>Per E-Mail teilen</Typography>
                            </MenuItem>
                          )}
                          {onDeleteDocument && (
                            <>
                              <Divider sx={{ my: 0.5 }} />
                              <MenuItem
                                onClick={() => {
                                  onDeleteDocument(item.id, index);
                                  handleMenuClose(item.id);
                                }}
                                sx={{ gap: 1.5, py: 1.5 }}
                              >
                                <DeleteIcon fontSize="small" sx={{ color: 'error.main' }} />
                                <Typography color="error.main">Löschen</Typography>
                              </MenuItem>
                            </>
                          )}
                        </>
                      )}
                    </Menu>
                  </>
                )}
              </Box>
            </ListItem>
          </Paper>
        ))}
      </Box>
    );
  };
